// client/a-router.ts
import { BehaviorSubject, combineLatest, fromEvent, merge, Observable } from "rxjs";
import { debounceTime, filter, map, skip, startWith, take } from "rxjs/operators";
import { A } from "../env-info";
var ReactiveURLSearchParams = class extends BehaviorSubject {
  constructor() {
    super(new URLSearchParams(window.location.search));
    this.paramsInstance = this.getValue();
  }
  next(newVal) {
    this.paramsInstance = newVal;
    super.next(newVal);
  }
  ping() {
    this.next(this.paramsInstance);
  }
  set(key, value) {
    if (this.paramsInstance.get(key) === value)
      return;
    this.paramsInstance.set(key, value);
    this.ping();
  }
  sort() {
    this.paramsInstance.sort();
    this.ping();
  }
  getOnce(key) {
    return this.paramsInstance.get(key);
  }
  get(key) {
    return this.pipe(map((qp) => qp.get(key)));
  }
  delete(key) {
    this.paramsInstance.delete(key);
    this.ping();
  }
  has(key) {
    return this.paramsInstance.has(key);
  }
  entries() {
    return this.paramsInstance.entries();
  }
  static isEqualTo(a = new URLSearchParams({}), b = new URLSearchParams({})) {
    return Array.from(a.entries()).every(([key, value]) => b.get(key) === value) && Array.from(b.entries()).every(([key, value]) => a.get(key) === value);
  }
};
var AdornisRouter = class {
  constructor() {
    this.patterns = [];
    this.variableSubjects = {};
    this.path = new BehaviorSubject(window.location.pathname);
    this.hash = new BehaviorSubject("");
    this.queryParams = new ReactiveURLSearchParams();
    this.metaTagMap = [];
    this.contentMeta = document.createElement("meta");
    this.titleMeta = document.createElement("meta");
    this.keywordsMeta = document.createElement("meta");
    document.head.appendChild(this.contentMeta);
    document.head.appendChild(this.titleMeta);
    document.head.appendChild(this.keywordsMeta);
    merge(fromEvent(window, "hashchange"), fromEvent(window, "location-changed"), fromEvent(window, "popstate")).pipe(
      startWith(decodeURI(window.location.pathname)),
      map((_) => decodeURI(window.location.pathname)),
      map((path) => {
        if (path.slice(-1)[0] === "/" && path.length > 1)
          return path.slice(0, -1);
        else
          return path;
      })
    ).subscribe((path) => {
      this.queryParams.next(new URLSearchParams(window.location.search));
      this.path.next(path);
    });
    combineLatest([fromEvent(window, "hashchange"), this.path]).pipe(map((_) => decodeURI(window.location.hash.substring(1)))).subscribe(this.hash);
    combineLatest([this.path, this.queryParams]).pipe(debounceTime(0)).subscribe(([path, queryParams]) => {
      AdornisRouter.goTo(path, queryParams);
    });
  }
  static init(patterns) {
    if (!AdornisRouter.instance)
      AdornisRouter.instance = new AdornisRouter();
    if (!patterns)
      return;
    AdornisRouter.instance.patterns = patterns;
    AdornisRouter.instance.listenToPathAndGetVariableValues(AdornisRouter.instance.patterns);
  }
  static addPatterns(patterns) {
    AdornisRouter.instance.patterns.push(...patterns);
    AdornisRouter.instance.listenToPathAndGetVariableValues(AdornisRouter.instance.patterns);
  }
  static get(variable) {
    if (!AdornisRouter.instance)
      this.init([]);
    if (!variable)
      return AdornisRouter.instance;
    else {
      if (!AdornisRouter.instance.variableSubjects[variable])
        AdornisRouter.instance.variableSubjects[variable] = new BehaviorSubject(null);
      return AdornisRouter.instance.variableSubjects[variable];
    }
  }
  getLastPart() {
    return AdornisRouter.get().path.getValue().split("/").slice(-1)[0];
  }
  listenToPathAndGetVariableValues(patterns) {
    this.subscription?.unsubscribe();
    this.subscription = this.path.subscribe((path) => {
      const variableSubjectKeys = new Set(Object.keys(this.variableSubjects));
      const matchingPatterns = patterns.filter((pattern) => this.doesPatternMatch(pattern, path));
      for (const pattern of matchingPatterns) {
        if (!this.doesPatternMatch(pattern, path))
          continue;
        for (const patternPart of pattern.split("/")) {
          if (patternPart.substring(0, 1) !== ":")
            continue;
          const varName = patternPart.substring(1);
          if (!(this.variableSubjects[varName] instanceof Observable)) {
            if (this.variableSubjects[varName])
              throw new Error("dont overwrite existing shit");
            this.variableSubjects[varName] = new BehaviorSubject(null);
          }
          const indices = [
            ...new Set(
              matchingPatterns.filter((pattern2) => pattern2.indexOf(":" + varName) > -1).map((pattern2) => pattern2.split("/").findIndex((part) => part === ":" + varName))
            )
          ];
          const newValue = decodeURIComponent(path.split("/")[indices[0]] || "");
          if (indices.length > 1)
            throw new Error("not sure which pattern to assign, more than one possibility");
          if (indices.length === 0)
            this.variableSubjects[varName].next(null);
          else if (this.variableSubjects[varName].getValue() !== newValue)
            this.variableSubjects[varName].next(newValue);
          variableSubjectKeys.delete(varName);
        }
      }
      variableSubjectKeys.forEach((key) => {
        if (this.variableSubjects[key].getValue() !== null)
          this.variableSubjects[key].next(null);
      });
      const availableMeta = this.getMetaTagForPath(path);
      if (availableMeta)
        this.updateMetaTags(availableMeta);
    });
  }
  getRegexForPattern(pattern) {
    return new RegExp("^" + pattern.replace(/\/\:[a-zA-Z0-9\-_]+/gi, "(/([a-zA-Z0-9%\\_\\-\\.\\~]+))?") + "/.*$");
  }
  doesPatternMatch(pattern, path) {
    const regex = this.getRegexForPattern(pattern);
    const match = !!path.match(regex);
    return match;
  }
  static goTo(href, queryParams = new URLSearchParams(location.search), replaceState = false) {
    if (!href)
      return;
    if (href.startsWith("http"))
      throw new Error("dont use goTo for external links");
    if (href === decodeURI(window.location.pathname) && ReactiveURLSearchParams.isEqualTo(queryParams, new URLSearchParams(window.location.search)))
      return;
    if (href.slice(-1)[0] === "/" && href.length !== 1)
      href = href.slice(0, -1);
    const newhref = [...queryParams.values()].length ? `${encodeURI(href)}?${queryParams.toString()}` : encodeURI(href);
    console.log("%c  router :: " + newhref, "color: #bada55");
    if (replaceState)
      window.history.replaceState({ path: newhref }, "", newhref);
    else
      window.history.pushState({ path: newhref }, "", newhref);
    window.dispatchEvent(new CustomEvent("location-changed", { node: window }));
  }
  static goToExternal(externalURL, target = "_blank", inAppBrowserOptions = "toolbarposition=top,location=no") {
    if (typeof cordova !== "undefined" && cordova.InAppBrowser) {
      const inAppBrowserRef = cordova.InAppBrowser.open(externalURL, "_blank", inAppBrowserOptions);
      fromEvent(inAppBrowserRef, "loadstart").pipe(
        skip(1),
        filter((e) => e.url.includes(A.absoluteUrl()) && !e.url.includes("/api/")),
        take(1)
      ).subscribe((e) => {
        inAppBrowserRef.close();
        this.goTo(`/${e.url.replace(A.absoluteUrl(), new URLSearchParams({}).toString())}`);
      });
    } else {
      window.open(externalURL, target);
    }
  }
  static openInNewWindow(path) {
    window.open(path, "_blank", "status=yes");
  }
  setDefaultMetaDesc(info) {
    this.defaultMeta = info;
    this.updateMetaTags(info);
  }
  updateMetaTags({ description: meta, title, keywords }) {
    requestAnimationFrame(() => {
      this.contentMeta.setAttribute("name", "description");
      this.contentMeta.setAttribute("content", meta);
      this.titleMeta.setAttribute("name", "title");
      this.titleMeta.setAttribute("content", title);
      this.keywordsMeta.setAttribute("name", "keywords");
      this.keywordsMeta.setAttribute("content", keywords || "");
      document.title = `${A.isDevelopment ? `<DEV/> - ` : ""}${title}`;
    });
  }
  setMeta({ title, path, description: meta, keywords }) {
    const existingIndex = this.metaTagMap.findIndex((x) => x.path === path);
    if (existingIndex !== -1)
      this.metaTagMap[existingIndex].description = meta;
    else
      this.metaTagMap.push({ title, path, description: meta, keywords });
  }
  getMetaTagForPath(path) {
    const existingIndex = this.metaTagMap.findIndex((x) => x.path === path);
    if (existingIndex !== -1)
      return this.metaTagMap[existingIndex];
    else
      return this.defaultMeta;
  }
};
export {
  AdornisRouter,
  ReactiveURLSearchParams
};
